import type React from "react";
import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils/index";

interface AddToCalendarLinkProps {
  /** The event's public "Add this calendar" link (opens Google Calendar's "add this calendar" flow). */
  href: string;
  /** Extra classes merged onto the anchor, for the two slightly different sizing contexts it's used in. */
  className?: string;
}

/**
 * "Add to calendar" link for the public schedule — only rendered by callers once the event's
 * calendar has actually been made public (`publicCalendarUrl` from `SchedulePublicDataProvider`).
 * Shared by `ScheduleRenderer` (inline section) and `FocusedScheduleView` (full-page header) so the
 * two surfaces don't drift in markup/styling.
 */
export function AddToCalendarLink({ href, className }: AddToCalendarLinkProps): React.ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex touch-manipulation items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50",
        className
      )}
    >
      <Calendar className="h-4 w-4" aria-hidden="true" />
      Add to calendar
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}
